// Copyright ©️ 2026 eVolve MEP, LLC

import { createContext, type ReactNode, useCallback, useMemo } from 'react';

import { type UnitOfMeasure, useEvolvePost } from '@evolvemep/api-ts';

import { useGeneralContext } from 'helpers/useGeneralContext';
import { useEvolveGetWithLocalStorage } from 'hooks/useEvolveGetWithLocalStorage';

export type NewUnitOfMeasureBody = Pick<UnitOfMeasure, 'name' | 'code'>;

type UnitOfMeasureContextType = {
  uoms: UnitOfMeasure[] | undefined;
  loading: boolean;
  addNewUOM: (newUom: NewUnitOfMeasureBody) => Promise<UnitOfMeasure>;
};

const UnitOfMeasureContext = createContext<UnitOfMeasureContextType | undefined>(undefined);

export const UnitOfMeasureProvider = ({ children }: { children: ReactNode }) => {
  const { apiCall: addNewUOMApiCall } = useEvolvePost('shop/partCatalog/uom');
  const { data: uoms, loading: loadingUOMs, updateStoredValue } = useEvolveGetWithLocalStorage('shop/partCatalog/uom');

  const addNewUOM = useCallback<UnitOfMeasureContextType['addNewUOM']>(
    async (values) =>
      addNewUOMApiCall(values).then((u) => {
        updateStoredValue((v = []) => [...v, u].toSorted((a, b) => a.name.localeCompare(b.name)));
        return u;
      }),
    [addNewUOMApiCall, updateStoredValue],
  );

  const value = useMemo<UnitOfMeasureContextType>(
    () => ({
      uoms,
      loading: loadingUOMs,
      addNewUOM,
    }),
    [uoms, loadingUOMs, addNewUOM],
  );
  return <UnitOfMeasureContext value={value}>{children}</UnitOfMeasureContext>;
};

export const useUnitOfMeasure = () => useGeneralContext(UnitOfMeasureContext, 'UnitOfMeasure');
